import { useState } from "react";
import { API, errMsg, rupiah, fmtDate, ORDER_STATUS } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { Search, Package, Truck, CheckCircle2, XCircle } from "lucide-react";

const STEPS = ["menunggu_ongkir", "menunggu_pembayaran", "menunggu_konfirmasi", "diproses", "dikirim", "selesai"];

export default function Lacak() {
  usePageMeta("Lacak Pesanan", "Lacak status pesanan NAWADAH by NDH Official secara mandiri — cukup masukkan nomor pesanan dan nomor HP Anda.");
  const [form, setForm] = useState({ order_number: "", phone: "" });
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    setRes(null);
    try {
      const { data } = await API.get("/orders/track", { params: { order_number: form.order_number.trim(), phone: form.phone.trim() } });
      setRes(data);
    } catch (error) {
      setErr(errMsg(error));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#040F1D] border border-white/15 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-[0.2em] text-gold block mb-2";
  const activeIdx = res ? STEPS.indexOf(res.status) : -1;

  return (
    <div className="bg-navy-dark text-white min-h-screen">
      <section className="relative py-20 bg-maritime-grid noise-overlay" data-testid="track-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-3">Lacak Pesanan</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">Cek Status Pesanan Anda</h1>
          <p className="text-slate-400 text-sm sm:text-base mb-10">Tanpa perlu login — masukkan nomor pesanan (contoh: NWD-260906-AB12) dan nomor HP yang dipakai saat memesan.</p>

          <form onSubmit={submit} className="card-maritime-surface rounded-md p-8 space-y-5" data-testid="track-form">
            <div>
              <label className={labelCls}>Nomor Pesanan</label>
              <input required value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} data-testid="track-order-number" className={inputCls} placeholder="NWD-XXXXXX-XXXX" />
            </div>
            <div>
              <label className={labelCls}>Nomor HP</label>
              <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="track-phone" className={inputCls} placeholder="08xx-xxxx-xxxx" />
            </div>
            <button type="submit" disabled={loading} data-testid="track-submit" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-60">
              <Search className="h-4 w-4" /> {loading ? "Mencari..." : "Lacak Pesanan"}
            </button>
            {err && <p className="text-sm text-red-400 text-center" data-testid="track-error">{err}</p>}
          </form>

          {res && (
            <div className="card-maritime-surface rounded-md p-8 mt-8" data-testid="track-result">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
                <div>
                  <p className="font-mono-accent font-bold text-lg" data-testid="track-number">{res.order_number}</p>
                  <p className="text-xs text-slate-400 mt-1">{res.customer_name} • dipesan {fmtDate(res.created_at)} • {res.items_count} item • {rupiah(res.total)}</p>
                </div>
                <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${(ORDER_STATUS[res.status] || {}).color || "bg-slate-100 text-slate-700"}`} data-testid="track-status">
                  {(ORDER_STATUS[res.status] || {}).label || res.status}
                </span>
              </div>

              {res.status === "dibatalkan" ? (
                <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-md p-4 text-sm text-red-300" data-testid="track-cancelled">
                  <XCircle className="h-5 w-5 shrink-0" /> Pesanan ini telah dibatalkan. Hubungi kami via WhatsApp bila ada pertanyaan.
                </div>
              ) : (
                <ol className="space-y-0" data-testid="track-timeline">
                  {STEPS.map((st, i) => {
                    const done = i <= activeIdx;
                    const current = i === activeIdx;
                    return (
                      <li key={st} className="flex gap-4" data-testid={`track-step-${st}`}>
                        <div className="flex flex-col items-center">
                          <div className={`h-8 w-8 rounded-full border-2 flex items-center justify-center shrink-0 ${done ? "bg-gold border-gold text-[#0B1F3A]" : "border-white/20 text-slate-500"}`}>
                            {done ? <CheckCircle2 className="h-4 w-4" /> : <Package className="h-4 w-4" />}
                          </div>
                          {i < STEPS.length - 1 && <div className={`w-0.5 h-8 ${i < activeIdx ? "bg-gold" : "bg-white/10"}`} />}
                        </div>
                        <div className="pb-8">
                          <p className={`text-sm font-bold ${current ? "text-gold" : done ? "text-white" : "text-slate-500"}`}>{ORDER_STATUS[st].label}</p>
                          {current && st === "dikirim" && res.courier && <p className="text-xs text-slate-400 mt-1"><Truck className="inline h-3.5 w-3.5 mr-1" />Dikirim via {res.courier}</p>}
                          {current && st === "menunggu_ongkir" && <p className="text-xs text-slate-400 mt-1">Pemilik sedang menghitung ongkir pesanan Anda.</p>}
                          {current && st === "menunggu_pembayaran" && <p className="text-xs text-slate-400 mt-1">Silakan selesaikan pembayaran & unggah bukti di halaman Pesanan Saya.</p>}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
